import React, { useState, useMemo, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import Layout from '@/components/Layout';
import Button from '@/components/Button';
import { ArrowLeft, ExternalLink, Calendar, Tag, Globe, CheckCircle2, AlertCircle, Clock, Star } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import SuggestEditDialog from '@/components/SuggestEditDialog';
import SuggestEditForm from '@/components/SuggestEditForm';
import { supabase } from '@/integrations/supabase/client';
import { toast } from '@/components/ui/use-toast';
import { SolutionData, SolutionDetails, ExternalLinkData, OfficialWebsiteData, CapabilityDetail, UseCaseDetail, VersionDetail } from '@/types/solution';
import { Json } from '@/integrations/supabase/types';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { useIsMobile } from '@/hooks/use-mobile';
import { trackSolutionPageView } from '@/services/clickTrackingService';
import { defaultImageService } from '@/services/defaultImageService';
import DynamicMetaTags from '@/components/DynamicMetaTags';
import MediaEmbed from '@/components/MediaEmbed';

// Below this width (the lg breakpoint) the desktop two-column grid
// (lg:grid-cols-12) is not active, so we use the stacked mobile layout.
const STACKED_LAYOUT_BREAKPOINT = 1024;

// Resolve the optional promo badge label for an official-website CTA.
// `true` -> default "with PROMO!"; a non-empty string -> custom text; else none.
const getPromoLabel = (promo?: boolean | string): string | null => {
  // Dev toggle: set to `true` to force-display the promo badge on every CTA
  // (regardless of data) while working locally. Keep `false` in committed code.
  const TEMP_FORCE_PROMO: boolean = false;
  if (TEMP_FORCE_PROMO) return 'with PROMO!';
  if (promo === true) return 'with PROMO!';
  if (typeof promo === 'string' && promo.trim().length > 0) return promo.trim();
  return null;
};

// Type guards for detailed objects
const isDetailsObject = (details: any): details is SolutionDetails => {
  return details !== null && typeof details === 'object' && !Array.isArray(details);
};

const isCapabilityArray = (capabilities: any): capabilities is CapabilityDetail[] => {
  return Array.isArray(capabilities) && 
    capabilities.length > 0 && 
    typeof capabilities[0] === 'object' && 
    'title' in capabilities[0];
};

const isUseCaseArray = (useCases: any): useCases is UseCaseDetail[] => {
  return Array.isArray(useCases) && 
    useCases.length > 0 && 
    typeof useCases[0] === 'object' && 
    'title' in useCases[0];
};

// Safely access nested properties with fallbacks
const safelyGetStringProperty = (obj: any, property: string, fallback: string = ''): string => {
  return obj && typeof obj === 'object' && typeof obj[property] === 'string' ? obj[property] : fallback;
};

const safelyGetArrayProperty = (obj: any, property: string, fallback: any[] = []): any[] => {
  return obj && typeof obj === 'object' && Array.isArray(obj[property]) ? obj[property] : fallback;
};

// Format date function
const formatDate = (dateString: string | undefined): string => {
  if (!dateString) return '';
  
  const options: Intl.DateTimeFormatOptions = { 
    year: 'numeric', 
    month: 'long', 
    day: 'numeric' 
  };
  
  return new Date(dateString).toLocaleDateString('en-US', options);
};

// Fetch solution details from Supabase
const fetchSolutionDetails = async (slug: string): Promise<SolutionData> => {
  console.log('Fetching solution details for slug:', slug);
  
  try {
    const { data: solution, error } = await supabase
      .from('solutions')
      .select('*')
      .eq('slug', slug)
      .or('status.is.null,status.neq.classified')
      .single();
      
    if (error) {
      console.error('Error fetching solution:', error);
      throw error;
    }
    
    if (!solution) {
      console.error('Solution not found');
      throw new Error('Solution not found');
    }
    
    console.log('Solution data received:', solution);
    
    // Get tags for this solution
    const { data: tagRelations, error: tagsError } = await supabase
      .from('solution_tags')
      .select(`
        tags(id, name)
      `)
      .eq('solution_id', solution.id);
      
    if (tagsError) {
      console.error('Error fetching tags:', tagsError);
      throw tagsError;
    }
    
    // Get categories for this solution
    const { data: categoryRelations, error: categoriesError } = await supabase
      .from('solution_categories')
      .select(`
        categories(id, name)
      `)
      .eq('solution_id', solution.id);
      
    if (categoriesError) {
      console.error('Error fetching categories:', categoriesError);
      throw categoriesError;
    }
    
    const tags = tagRelations
      .map(relation => relation.tags)
      .filter(Boolean)
      .map(tag => tag.name);
      
    const categories = categoryRelations
      .map(relation => relation.categories)
      .filter(Boolean)
      .map(category => category.name);
    
    // Use the details from the database
    let details: SolutionDetails = {};
    
    if (solution.details && isDetailsObject(solution.details)) {
      details = solution.details as SolutionDetails;
    }
    
    // Get officialWebsite and externalLinks from database
    let officialWebsite: OfficialWebsiteData | undefined;
    
    if (solution.details && isDetailsObject(solution.details) && 
        solution.details.officialWebsite && 
        typeof solution.details.officialWebsite === 'object') {
      officialWebsite = solution.details.officialWebsite as OfficialWebsiteData;
    }
    
    let externalLinks: ExternalLinkData[] = [];
    
    if (solution.details && isDetailsObject(solution.details) && 
        solution.details.externalLinks && 
        Array.isArray(solution.details.externalLinks)) {
      externalLinks = solution.details.externalLinks as ExternalLinkData[];
    }
    
    // Get creator name from database
    const category = categories[0] || 'Others (U)';
    let createdBy = 'AI Model Mind Space';
    
    if (solution.details && isDetailsObject(solution.details) && 
        typeof solution.details.createdBy === 'string') {
      createdBy = solution.details.createdBy;
    }
    
    return {
      id: solution.id,
      slug: solution.slug,
      name: solution.name,
      category,
      shortDescription: solution.short_description,
      tags,
      createdBy,
      createdAt: new Date(solution.created_at).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      }),
      image: solution.image_url,
      officialWebsite,
      externalLinks,
      details
    };
  } catch (error) {
    console.error('Error in fetchSolutionDetails:', error);
    throw error;
  }
};

// SolutionDetail component
const SolutionDetail = () => {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const isMobile = useIsMobile(STACKED_LAYOUT_BREAKPOINT);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [expandedCapability, setExpandedCapability] = useState<string | null>(null);
  const [expandedUseCase, setExpandedUseCase] = useState<string | null>(null);
  const [isVersionsOpen, setIsVersionsOpen] = useState(false);
  const [imageError, setImageError] = useState(false);
  
  // Get cached default image URL (24-hour cache)
  const defaultAIImage = defaultImageService.getDefaultImageUrlSync();
  
  // Scroll to top when the component mounts or when the slug changes
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [slug]);
  
  const { data: solution, isLoading, error } = useQuery({
    queryKey: ['solution', slug],
    queryFn: () => slug ? fetchSolutionDetails(slug) : null,
    enabled: !!slug,
    retry: 1,
  });

  // Track page view for trending analytics using solution ID (UUID)
  useEffect(() => {
    if (solution?.id) {
      trackSolutionPageView(solution.id).catch(error => {
        console.error('Failed to track page view:', error);
        // Don't show user-facing error for analytics tracking
      });
    }
  }, [solution?.id]);
  
  // Finding the latest version to display when collapsed
  const latestVersion = useMemo(() => {
    if (!solution?.details?.versions || (solution.details.versions as VersionDetail[]).length === 0) {
      return null;
    }
    
    const versions = solution.details.versions as VersionDetail[];
    return versions.find(v => v.tags && Array.isArray(v.tags) && v.tags.includes('latest')) || versions[0];
  }, [solution]);

  const handleEditClick = () => {
    setIsEditDialogOpen(true);
  };

  const handleEditClose = () => {
    setIsEditDialogOpen(false);
  };
  
  const toggleCapability = (title: string) => {
    if (expandedCapability === title) {
      setExpandedCapability(null);
    } else {
      setExpandedCapability(title);
    }
  };
  
  const toggleUseCase = (title: string) => {
    if (expandedUseCase === title) {
      setExpandedUseCase(null);
    } else {
      setExpandedUseCase(title);
    }
  };

  if (isLoading) {
    return (
      <Layout className="pt-16">
        <div className="container mx-auto px-4">
          <div className="animate-pulse space-y-8">
            <div className="h-8 bg-secondary rounded w-48"></div>
            <div className="h-10 bg-secondary rounded w-2/3"></div>
            <div className="h-6 bg-secondary rounded w-full"></div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              <div className="md:col-span-2 space-y-6">
                <div className="h-40 bg-secondary rounded"></div>
                <div className="h-60 bg-secondary rounded"></div>
              </div>
              <div className="space-y-6">
                <div className="h-40 bg-secondary rounded"></div>
                <div className="h-40 bg-secondary rounded"></div>
              </div>
            </div>
          </div>
        </div>
      </Layout>
    );
  }

  if (error || !solution) {
    return (
      <Layout className="pt-16">
        <div className="container mx-auto px-4 py-16 text-center">
          <h1 className="text-2xl font-medium mb-4">Solution not found</h1>
          <p className="text-muted-foreground mb-6">The solution you're looking for doesn't exist or has been removed.</p>
          <Link to="/">
            <Button>
              Return Home
            </Button>
          </Link>
        </div>
      </Layout>
    );
  }

  // Render the sidebar content (to be used in both desktop and mobile layouts)
  const renderSidebarContent = () => {
    // Use default image if no image provided or if image failed to load
    const displayImage = imageError || !solution?.image ? defaultAIImage : solution.image;
    
    return (
      <div className="space-y-6">
        <div className="rounded-xl border border-border bg-card overflow-hidden mb-6 animate-fade-up opacity-0" style={{ animationDelay: '0.2s', animationFillMode: 'forwards' }}>
          <div className="aspect-video w-full overflow-hidden">
            <img 
              src={displayImage}
              alt={solution?.name || 'Solution'}
              className="w-full h-full object-contain ai-solution-image"
              onError={() => setImageError(true)}
            />
          </div>
        </div>
        
        {/* Official Website Link */}
        <div className="rounded-xl border border-primary bg-card overflow-hidden animate-fade-up opacity-0" style={{ animationDelay: '0.25s', animationFillMode: 'forwards' }}>
          {solution.officialWebsite ? (
            <a
              href={solution.officialWebsite.url}
              target="_blank"
              rel="noopener noreferrer"
              className="group flex w-full items-center justify-between gap-4 bg-primary px-5 py-4 text-primary-foreground transition-all hover:bg-primary/90"
            >
              <div className="flex flex-1 items-center gap-3">
                <div className="rounded-md bg-primary-foreground/20 p-2 transition-colors">
                  <Globe className="h-4 w-4" />
                </div>
                <div className="flex-1 text-center leading-tight">
                  <span className="block text-lg font-semibold tracking-wide">Access it Here</span>
                  {getPromoLabel(solution.officialWebsite.promo) && (
                    <span className="mt-1.5 inline-flex items-center gap-1 rounded-full bg-gradient-to-r from-amber-300 to-yellow-500 px-2.5 py-0.5 text-xs font-bold text-amber-950 shadow-sm ring-1 ring-amber-200/60">
                      <Star className="h-3 w-3" />
                      {getPromoLabel(solution.officialWebsite.promo)}
                    </span>
                  )}
                </div>
              </div>
              <ExternalLink className="h-4 w-4 transition-transform group-hover:translate-x-0.5" />
            </a>
          ) : (
            <div className="flex items-center gap-2 p-5 text-muted-foreground">
              <Globe className="h-5 w-5" />
              <span className="font-medium">No website</span>
            </div>
          )}
        </div>
        
        {/* External Resources */}
        <div className="rounded-xl border border-border bg-card overflow-hidden animate-fade-up opacity-0" style={{ animationDelay: '0.3s', animationFillMode: 'forwards' }}>
          <div className="p-5 border-b border-border">
            <h3 className="font-medium">External Resources</h3>
          </div>
          <div className="p-5">
            <ul className="space-y-3">
              {solution.externalLinks?.map((link, index) => (
                <li key={index}>
                  <a 
                    href={link.url} 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="flex items-center justify-between group text-muted-foreground hover:text-primary transition-colors"
                  >
                    <span>{link.title}</span>
                    <ExternalLink className="h-4 w-4 group-hover:text-primary transition-colors" />
                  </a>
                </li>
              ))}
            </ul>
          </div>
        </div>
        
        {/* Related Solutions */}
        <div className="rounded-xl border border-border bg-card overflow-hidden animate-fade-up opacity-0" style={{ animationDelay: '0.4s', animationFillMode: 'forwards' }}>
          <div className="p-5 border-b border-border">
            <h3 className="font-medium">Related Solutions</h3>
          </div>
          <div className="p-5">
            <p className="text-muted-foreground text-sm mb-3">
              Check out other solutions in the {solution.category} category:
            </p>
            <div className="space-y-2">
              <p className="text-sm text-primary">
                <Link to="/explore">
                  Explore all {solution.category} solutions
                </Link>
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  };

  // Render header content
  const renderHeaderContent = () => (
    <div className="animate-fade-up">
      <div className="mb-3">
        <span className="text-sm font-medium px-3 py-1 bg-secondary rounded-full text-secondary-foreground">
          {solution.category}
        </span>
      </div>
      
      <h1 className="text-3xl md:text-4xl font-medium tracking-tight mb-4">
        {solution.name}
      </h1>
      
      <p className="text-muted-foreground text-lg mb-4 max-w-3xl">
        {solution.shortDescription}
      </p>
      
      <div className="flex flex-wrap gap-2 mb-4">
        {solution.tags.map((tag) => (
          <span 
            key={tag} 
            className="flex items-center gap-1 text-xs px-2.5 py-1 bg-primary/10 text-primary rounded-full"
          >
            <Tag className="h-3 w-3" />
            {tag}
          </span>
        ))}
      </div>
      
      <div className="flex items-center gap-6 text-sm text-muted-foreground mb-6">
        <div className="flex items-center gap-1.5">
          <span className="font-medium">Developer:</span> {solution.createdBy}
        </div>
        <div className="flex items-center gap-1.5">
          <Calendar className="h-3.5 w-3.5" />
          <span>Added: {solution.createdAt}</span>
        </div>
      </div>
      
    </div>
  );

  // Render main content (to be used in both layouts)
  const renderMainContent = () => (
    <>
      {/* Versions Section (if available) */}
      {solution.details?.versions && solution.details.versions.length > 0 && (
        <div className="mb-10 animate-fade-up opacity-0" style={{ animationDelay: '0.2s', animationFillMode: 'forwards' }}>
          <Collapsible open={isVersionsOpen} onOpenChange={setIsVersionsOpen}>
            <div className="flex items-center justify-between">
              <h2 className="text-2xl font-medium mb-4">Versions</h2>
              <CollapsibleTrigger asChild>
                <Button variant="ghost" size="sm" className="p-0 h-9 w-9">
                  <ArrowLeft className={`h-4 w-4 transition-transform ${isVersionsOpen ? 'rotate-90' : '-rotate-90'}`} />
                </Button>
              </CollapsibleTrigger>
            </div>
            
            {/* Always show the latest version, even when collapsed */}
            {!isVersionsOpen && latestVersion && (
              <div className="border rounded-lg overflow-hidden mb-2">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Version</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Release Date</TableHead>
                      <TableHead>Tags</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    <TableRow>
                      <TableCell className="font-medium">{latestVersion.version}</TableCell>
                      <TableCell>
                        <span className={`inline-flex px-2 py-1 rounded-full text-xs ${
                          latestVersion.status === 'released' 
                            ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400' 
                            : 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400'
                        }`}>
                          {latestVersion.status}
                        </span>
                      </TableCell>
                      <TableCell className="text-muted-foreground">
                        {formatDate(latestVersion.since)}
                      </TableCell>
                      <TableCell>
                        <div className="flex flex-wrap gap-1">
                          {latestVersion.tags && Array.isArray(latestVersion.tags) && latestVersion.tags.map((tag, tagIndex) => (
                            <span 
                              key={tagIndex} 
                              className={`text-xs px-2 py-0.5 rounded-full ${
                                tag === 'latest' 
                                  ? 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400' 
                                  : tag === 'deprecated' 
                                    ? 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400' 
                                    : 'bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-200'
                              }`}
                            >
                              {tag}
                            </span>
                          ))}
                        </div>
                      </TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </div>
            )}
            
            <CollapsibleContent>
              <div className="border rounded-lg overflow-hidden mb-2">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Version</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Release Date</TableHead>
                      <TableHead>Tags</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {(solution.details?.versions as VersionDetail[]).map((version, index) => (
                      <TableRow key={index}>
                        <TableCell className="font-medium">{version.version}</TableCell>
                        <TableCell>
                          <span className={`inline-flex px-2 py-1 rounded-full text-xs ${
                            version.status === 'released' 
                              ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400' 
                              : 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400'
                          }`}>
                            {version.status}
                          </span>
                        </TableCell>
                        <TableCell className="text-muted-foreground">
                          {formatDate(version.since)}
                        </TableCell>
                        <TableCell>
                          <div className="flex flex-wrap gap-1">
                            {version.tags && Array.isArray(version.tags) && version.tags.map((tag, tagIndex) => (
                              <span 
                                key={tagIndex} 
                                className={`text-xs px-2 py-0.5 rounded-full ${
                                  tag === 'latest' 
                                    ? 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400' 
                                    : tag === 'deprecated' 
                                      ? 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400' 
                                      : 'bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-200'
                                }`}
                              >
                                {tag}
                              </span>
                            ))}
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </CollapsibleContent>
          </Collapsible>
        </div>
      )}
      
      {/* Overview */}
      <div className="prose prose-gray max-w-none mb-10 animate-fade-up opacity-0" style={{ animationDelay: '0.25s', animationFillMode: 'forwards' }}>
        <h2 className="text-2xl font-medium mb-4">Overview</h2>
        <div className="whitespace-pre-line text-muted-foreground leading-relaxed">
          {solution.details?.overview || solution.shortDescription}
        </div>
      </div>
      
      {/* Capabilities Section */}
      <div className="mb-10 animate-fade-up opacity-0" style={{ animationDelay: '0.3s', animationFillMode: 'forwards' }}>
        <h2 className="text-2xl font-medium mb-4">Key Capabilities</h2>
        
        {solution.details?.capabilities && (
          <div className="space-y-4">
            {isCapabilityArray(solution.details.capabilities) ? (
              // Render detailed capabilities with descriptions
              solution.details.capabilities.map((capability, index) => (
                <div key={index} className="border border-border/50 rounded-lg overflow-hidden">
                  <div 
                    className="flex items-start justify-between p-4 cursor-pointer hover:bg-muted/30 transition-colors"
                    onClick={() => toggleCapability(capability.title)}
                  >
                    <div className="flex items-start">
                      <CheckCircle2 className="h-5 w-5 text-primary/70 mr-3 mt-0.5 flex-shrink-0" />
                      <div className="flex-1">
                        <h3 className="font-medium text-foreground">{capability.title}</h3>
                        {expandedCapability !== capability.title && (
                          <p className="text-muted-foreground text-sm line-clamp-1">{capability.description}</p>
                        )}
                      </div>
                    </div>
                    <ArrowLeft className={`h-4 w-4 mt-1 transition-transform ${expandedCapability === capability.title ? 'rotate-90' : '-rotate-90'}`} />
                  </div>
                  
                  {/* Single MediaEmbed instance (visible in both states) */}
                  {capability.mediaLink && (
                    <div className="px-4 pb-4">
                      <MediaEmbed 
                        url={capability.mediaLink} 
                        title=""
                        className={expandedCapability === capability.title ? "mb-3" : ""}
                      />
                    </div>
                  )}

                  {expandedCapability === capability.title && (
                    <div className="px-4 pb-4 pt-0 bg-muted/10">
                      <p className="text-muted-foreground mb-3">{capability.description}</p>
                      
                      {(capability.releaseDate || capability.lastUpdated) && (
                        <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-muted-foreground">
                          {capability.releaseDate && (
                            <div className="flex items-center gap-1">
                              <Calendar className="h-3 w-3" />
                              <span>Released: {formatDate(capability.releaseDate)}</span>
                            </div>
                          )}
                          {capability.lastUpdated && (
                            <div className="flex items-center gap-1">
                              <Clock className="h-3 w-3" />
                              <span>Updated: {formatDate(capability.lastUpdated)}</span>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              ))
            ) : (
              // Render simple capabilities list
              <ul className="space-y-2 text-muted-foreground">
                {solution.details.capabilities.map((capability, index) => (
                  <li key={index} className="flex items-start">
                    <CheckCircle2 className="h-5 w-5 text-primary/70 mr-3 mt-0.5 flex-shrink-0" />
                    {capability}
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}
      </div>
      
      {/* Use Cases Section */}
      <div className="mb-10 animate-fade-up opacity-0" style={{ animationDelay: '0.4s', animationFillMode: 'forwards' }}>
        <h2 className="text-2xl font-medium mb-4">Common Use Cases</h2>
        
        {solution.details?.useCases && (
          isUseCaseArray(solution.details.useCases) ? (
            // Render detailed use cases
            <div className="space-y-4">
              {solution.details.useCases.map((useCase, index) => (
                <div key={index} className="border border-border/50 rounded-lg overflow-hidden bg-card">
                  <div 
                    className="flex items-start justify-between p-4 cursor-pointer hover:bg-muted/10 transition-colors"
                    onClick={() => toggleUseCase(useCase.title)}
                  >
                    <div className="flex-1">
                      <h3 className="font-medium text-foreground">{useCase.title}</h3>
                      {expandedUseCase !== useCase.title && (
                        <p className="text-muted-foreground text-sm line-clamp-1">{useCase.description}</p>
                      )}
                    </div>
                    <ArrowLeft className={`h-4 w-4 mt-1 transition-transform ${expandedUseCase === useCase.title ? 'rotate-90' : '-rotate-90'}`} />
                  </div>
                  
                  {/* Single MediaEmbed instance (visible in both states) */}
                  {useCase.mediaLink && (
                    <div className="px-4 pb-4">
                      <MediaEmbed 
                        url={useCase.mediaLink} 
                        title={expandedUseCase === useCase.title ? `${useCase.title} example` : ""}
                        className={expandedUseCase === useCase.title ? "mb-3" : ""}
                      />
                    </div>
                  )}

                  {expandedUseCase === useCase.title && (
                    <div className="px-4 pb-4 pt-0 bg-muted/5">
                      <p className="text-muted-foreground mb-3">{useCase.description}</p>
                      
                      {(useCase.releaseDate || useCase.lastUpdated) && (
                        <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-muted-foreground">
                          {useCase.releaseDate && (
                            <div className="flex items-center gap-1">
                              <Calendar className="h-3 w-3" />
                              <span>Released: {formatDate(useCase.releaseDate)}</span>
                            </div>
                          )}
                          {useCase.lastUpdated && (
                            <div className="flex items-center gap-1">
                              <Clock className="h-3 w-3" />
                              <span>Updated: {formatDate(useCase.lastUpdated)}</span>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              ))}
            </div>
          ) : (
            // Render simple use cases grid
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {solution.details.useCases.map((useCase, index) => (
                <div key={index} className="p-4 rounded-lg border border-border/50 bg-card">
                  <p className="text-foreground">{useCase}</p>
                </div>
              ))}
            </div>
          )
        )}
      </div>
      
      {/* Limitations Section - Hidden */}
      {false && (
        <div className="mb-6 animate-fade-up opacity-0" style={{ animationDelay: '0.5s', animationFillMode: 'forwards' }}>
          <h2 className="text-2xl font-medium mb-4">Limitations</h2>
          <div className="p-4 rounded-lg border border-yellow-200/30 bg-yellow-50/30 dark:bg-yellow-900/10">
            <div className="flex gap-3">
              <AlertCircle className="h-5 w-5 text-yellow-600 dark:text-yellow-400 flex-shrink-0 mt-0.5" />
              <div>
                <h3 className="font-medium text-yellow-700 dark:text-yellow-300 mb-2">Important considerations</h3>
                <ul className="list-disc pl-5 space-y-1 text-muted-foreground">
                  {solution.details?.limitations?.map((limitation, index) => (
                    <li key={index}>{limitation}</li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );

  return (
          <Layout className="pt-16">
        <DynamicMetaTags
          title={`${solution.name} - AI Solution | AIZen.tips`}
          description={solution.shortDescription || `Discover ${solution.name}, an AI solution for your needs. Explore features, capabilities, and implementation details.`}
          canonical={`https://aizen.tips/solution/${solution?.slug ?? slug}`}
          ogImage={solution.image || defaultAIImage}
        />
      <div className="container mx-auto px-4 py-8">
        {/* Back navigation - Desktop only */}
        {!isMobile && (
          <div className="mb-6 animate-fade-in">
            <Button 
              variant="ghost" 
              icon={<ArrowLeft size={16} />} 
              iconPosition="left" 
              className="pl-0"
              onClick={() => {
                // Use browser's native back functionality to preserve scroll position
                if (window.history.length > 1) {
                  window.history.back();
                } else {
                  navigate('/');
                }
              }}
            >
              Back to Solutions
            </Button>
          </div>
        )}
        
        {/* MOBILE: Stacked layout for small screens */}
        {isMobile ? (
          <>
            {/* Row 1: Back Navigation + Edit Button */}
            <div className="flex items-center justify-between mb-6 animate-fade-in">
              <Button 
                variant="ghost" 
                icon={<ArrowLeft size={16} />} 
                iconPosition="left" 
                className="pl-0"
                onClick={() => {
                  if (window.history.length > 1) {
                    window.history.back();
                  } else {
                    navigate('/');
                  }
                }}
              >
                Back to Solutions
              </Button>
              <SuggestEditDialog solution={solution} />
            </div>

            {/* Row 2: Solution Image */}
            <div className="mb-6 animate-fade-up opacity-0" style={{ animationDelay: '0.1s', animationFillMode: 'forwards' }}>
              <div className="rounded-xl border border-border bg-card overflow-hidden">
                <div className="aspect-video w-full overflow-hidden">
                  <img 
                    src={imageError || !solution?.image ? defaultAIImage : solution.image}
                    alt={solution?.name || 'Solution'}
                    className="w-full h-full object-contain ai-solution-image"
                    onError={() => setImageError(true)}
                  />
                </div>
              </div>
            </div>

            {/* Row 3: Official Website Link */}
            {solution.officialWebsite && (
              <div className="mb-6 animate-fade-up opacity-0" style={{ animationDelay: '0.15s', animationFillMode: 'forwards' }}>
                <div className="rounded-xl border border-primary bg-card overflow-hidden">
                  <a
                    href={solution.officialWebsite.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="group flex w-full items-center justify-between gap-4 bg-primary px-5 py-4 text-primary-foreground transition-all hover:bg-primary/90"
                  >
                    <div className="flex flex-1 items-center gap-3">
                      <div className="rounded-md bg-primary-foreground/20 p-2 transition-colors">
                        <Globe className="h-4 w-4" />
                      </div>
                      <div className="flex-1 text-center leading-tight">
                        <span className="block text-lg font-semibold tracking-wide">Access it Here</span>
                        {getPromoLabel(solution.officialWebsite.promo) && (
                          <span className="mt-1.5 inline-flex items-center gap-1 rounded-full bg-gradient-to-r from-amber-300 to-yellow-500 px-2.5 py-0.5 text-xs font-bold text-amber-950 shadow-sm ring-1 ring-amber-200/60">
                            <Star className="h-3 w-3" />
                            {getPromoLabel(solution.officialWebsite.promo)}
                          </span>
                        )}
                      </div>
                    </div>
                    <ExternalLink className="h-4 w-4 transition-transform group-hover:translate-x-0.5" />
                  </a>
                </div>
              </div>
            )}

            {/* Header Section: Category + Title + Description + Tags + Metadata */}
            <div className="animate-fade-up opacity-0 mb-6" style={{ animationDelay: '0.2s', animationFillMode: 'forwards' }}>
              <div className="mb-3">
                <span className="text-sm font-medium px-3 py-1 bg-secondary rounded-full text-secondary-foreground">
                  {solution.category}
                </span>
              </div>
              
              <h1 className="text-3xl md:text-4xl font-medium tracking-tight mb-4">
                {solution.name}
              </h1>
              
              <p className="text-muted-foreground text-lg mb-4 max-w-3xl">
                {solution.shortDescription}
              </p>
              
              <div className="flex flex-wrap gap-2 mb-4">
                {solution.tags.map((tag) => (
                  <span 
                    key={tag} 
                    className="flex items-center gap-1 text-xs px-2.5 py-1 bg-primary/10 text-primary rounded-full"
                  >
                    <Tag className="h-3 w-3" />
                    {tag}
                  </span>
                ))}
              </div>
              
              <div className="flex items-center gap-6 text-sm text-muted-foreground mb-6">
                <div className="flex items-center gap-1.5">
                  <span className="font-medium">Developer:</span> {solution.createdBy}
                </div>
                <div className="flex items-center gap-1.5">
                  <Calendar className="h-3.5 w-3.5" />
                  <span>Added: {solution.createdAt}</span>
                </div>
              </div>
            </div>
            
            {/* Main Content Area */}
            <div className="mt-4">
              {renderMainContent()}
            </div>
            
            {/* External Resources */}
            {solution.externalLinks && solution.externalLinks.length > 0 && (
              <div className="mt-8 animate-fade-up opacity-0" style={{ animationDelay: '0.3s', animationFillMode: 'forwards' }}>
                <div className="rounded-xl border border-border bg-card overflow-hidden">
                  <div className="p-5 border-b border-border">
                    <h3 className="font-medium">External Resources</h3>
                  </div>
                  <div className="p-5">
                    <ul className="space-y-3">
                      {solution.externalLinks.map((link, index) => (
                        <li key={index}>
                          <a 
                            href={link.url} 
                            target="_blank" 
                            rel="noopener noreferrer"
                            className="flex items-center justify-between group text-muted-foreground hover:text-primary transition-colors"
                          >
                            <span>{link.title}</span>
                            <ExternalLink className="h-4 w-4 group-hover:text-primary transition-colors" />
                          </a>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              </div>
            )}

            {/* Related Solutions (at bottom) */}
            <div className="mt-8 animate-fade-up opacity-0" style={{ animationDelay: '0.4s', animationFillMode: 'forwards' }}>
              <div className="rounded-xl border border-border bg-card overflow-hidden">
                <div className="p-5 border-b border-border">
                  <h3 className="font-medium">Related Solutions</h3>
                </div>
                <div className="p-5">
                  <p className="text-muted-foreground text-sm mb-3">
                    Check out other solutions in the {solution.category} category:
                  </p>
                  <div className="space-y-2">
                    <p className="text-sm text-primary">
                      <Link to="/explore">
                        Explore all {solution.category} solutions
                      </Link>
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </>
        ) : (
          <>
            {/* DESKTOP: Two-column layout */}
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
              {/* Left column - Main content */}
              <div className="lg:col-span-8">
                {/* Header Section */}
                {renderHeaderContent()}
                
                {/* Main Content */}
                {renderMainContent()}
              </div>
              
              {/* Right column - Suggest Edit Button and Sidebar content */}
              <div className="lg:col-span-4">
                {/* Suggest Edit Button positioned above the sidebar content */}
                <div className="flex justify-end mb-4 animate-fade-up opacity-0" style={{ animationDelay: '0.15s', animationFillMode: 'forwards' }}>
                  <SuggestEditDialog solution={solution} />
                </div>
                
                <div className="sticky top-24">
                  {renderSidebarContent()}
                </div>
              </div>
            </div>
          </>
        )}
      </div>

      {/* Edit Suggestion Dialog */}
      <Dialog open={isEditDialogOpen} onOpenChange={setIsEditDialogOpen}>
        <DialogContent className="sm:max-w-[700px] max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Suggest an Edit for {solution.name}</DialogTitle>
            <DialogDescription>
              Help us improve our information about this solution by suggesting edits or corrections.
            </DialogDescription>
          </DialogHeader>
          <SuggestEditForm 
            solutionId={solution.id} 
            solutionName={solution.name}
            solutionShortDescription={solution.shortDescription}
            solutionImage={solution.image || ""}
            solutionDetails={solution.details}
            onSuccess={handleEditClose} 
          />
        </DialogContent>
      </Dialog>
    </Layout>
  );
};

export default SolutionDetail;
